import { useQueries, useQuery } from '@tanstack/react-query';
import { getMyProfile } from '@/lib/hrApi';
import { creditApi } from '@/features/credit/api/creditApi';
import type { CreditFacility } from '@/features/credit/types';
import {
  listMyPayslips,
  getMyPayslip,
  getMyYtd,
  getMyAnalytics,
  getMyCompensation,
  getIncomeStatement,
  COMPENSATION_DISABLED,
  isEmployeeNotLinked,
  retryUnlessNotLinked,
} from './payApi';
import { listMyTickets } from './ticketsApi';

// "Me" resolves to the logged-in employee's id (employee-service profile), which
// is the same id payroll keys payslips on.
export function useMyEmployeeId() {
  return useQuery({
    queryKey: ['mypay', 'me'],
    queryFn: () => getMyProfile(),
    select: (e) => e?.id ?? null,
    staleTime: 10 * 60_000,
  });
}

export function useMyPayslips() {
  const { data: employeeId } = useMyEmployeeId();
  return useQuery({
    queryKey: ['mypay', 'payslips', employeeId],
    queryFn: () => listMyPayslips(employeeId as string),
    enabled: !!employeeId,
    retry: retryUnlessNotLinked,
  });
}

export function useMyPayslip(id: string | null) {
  return useQuery({
    queryKey: ['mypay', 'payslip', id],
    queryFn: () => getMyPayslip(id as string),
    enabled: !!id,
  });
}

export function useMyYtd(year: number | null) {
  const { data: employeeId } = useMyEmployeeId();
  return useQuery({
    queryKey: ['mypay', 'ytd', employeeId, year],
    queryFn: () => getMyYtd(employeeId as string, year as number),
    enabled: !!employeeId && !!year,
    retry: retryUnlessNotLinked,
  });
}

export function useMyAnalytics(months: number) {
  const { data: employeeId } = useMyEmployeeId();
  return useQuery({
    queryKey: ['mypay', 'analytics', employeeId, months],
    queryFn: () => getMyAnalytics(employeeId as string, months),
    enabled: !!employeeId,
    retry: retryUnlessNotLinked,
  });
}

// ---- Credit (loans & advances) — pay-context reads. Fetched independently of
// payroll so a credit outage degrades to a section-level "unavailable" state
// and never blocks the payslip data. `enabled` gates on the tenant's credit
// entitlement so non-credit tenants never call /credit/*.

export function useMyFacilities(enabled: boolean) {
  return useQuery({
    queryKey: ['mypay', 'credit', 'facilities'],
    queryFn: () => creditApi.myFacilities(),
    enabled,
    staleTime: 60_000,
  });
}

export function useMyOffers(enabled: boolean) {
  return useQuery({
    queryKey: ['mypay', 'credit', 'offers'],
    queryFn: () => creditApi.myOffers(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useMyApplications(enabled: boolean) {
  return useQuery({
    queryKey: ['mypay', 'credit', 'applications'],
    queryFn: () => creditApi.myApplications(),
    enabled,
    staleTime: 60_000,
  });
}

export function useMyAgreements(enabled: boolean) {
  return useQuery({
    queryKey: ['mypay', 'credit', 'agreements'],
    queryFn: () => creditApi.myAgreements(),
    enabled,
    staleTime: 60_000,
  });
}

/** One facility's repayment schedule, fetched on demand (expanded row). */
export function useMySchedule(facilityId: string | null) {
  return useQuery({
    queryKey: ['mypay', 'credit', 'schedule', facilityId],
    queryFn: () => creditApi.mySchedule(facilityId as string),
    enabled: !!facilityId,
    staleTime: 60_000,
  });
}

/** Schedules for the OPEN facilities (bounded set) — powers the upcoming-
 *  deductions timeline. Individual failures resolve to null (partial data). */
export function useOpenFacilitySchedules(facilities: CreditFacility[] | undefined) {
  const open = (facilities ?? []).filter((f) =>
    ['active', 'in_arrears', 'settling', 'pending_disbursement', 'disbursing'].includes(f.status),
  );
  return useQueries({
    queries: open.map((f) => ({
      queryKey: ['mypay', 'credit', 'schedule', f.id],
      queryFn: () => creditApi.mySchedule(f.id),
      staleTime: 60_000,
    })),
    combine: (results) => ({
      facilities: open,
      schedules: results.map((r) => r.data ?? null),
      loading: results.some((r) => r.isLoading),
    }),
  });
}

// ---- Phase 5/6 self-service ------------------------------------------------

/** Own compensation. error.message === COMPENSATION_DISABLED when the tenant
 *  policy hides it (the shell removes the tab; no retries on that). */
export function useMyCompensation() {
  return useQuery({
    queryKey: ['mypay', 'compensation'],
    queryFn: () => getMyCompensation(),
    staleTime: 5 * 60_000,
    retry: (count, err) =>
      (err as Error)?.message !== COMPENSATION_DISABLED && !isEmployeeNotLinked(err) && count < 2,
  });
}

/** Income-statement preview (json). Enabled only once a range is chosen. */
export function useIncomeStatement(from: string | null, to: string | null) {
  return useQuery({
    queryKey: ['mypay', 'income-statement', from, to],
    queryFn: () => getIncomeStatement(from as string, to as string),
    enabled: !!from && !!to,
    staleTime: 60_000,
  });
}

/** The caller's own support tickets (payroll issues etc.). */
export function useMyTickets(enabled: boolean) {
  return useQuery({
    queryKey: ['mypay', 'tickets'],
    queryFn: () => listMyTickets(),
    enabled,
    staleTime: 30_000,
  });
}
